import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Percent, 
  Clock, 
  Users, 
  Download, 
  Building2, 
  Coffee, 
  Utensils, 
  Sparkles, 
  FileText,
  BedDouble,
  ArrowRight,
  Package,
  AlertTriangle,
  Search,
  Layers,
  Activity,
  Boxes,
  Compass
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { asArray, pluralizar } from '../utils/storage';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import { Reservation, Client, Accommodation, AccommodationType, StockItem, StockCategory, Minibar } from '../types';

interface ReportsProps {
  reservations: Reservation[];
  clients: Client[];
  accommodations: Accommodation[];
  accommodationTypes: AccommodationType[];
  stockItems: StockItem[];
  stockCategories: StockCategory[];
  minibars: Minibar[];
  profile: {
    name: string;
    corporateName: string;
    document: string;
    phone: string;
    email: string;
    address: string;
  };
}

export default function Reports({
  reservations,
  clients,
  accommodations,
  accommodationTypes,
  stockItems,
  stockCategories,
  minibars,
  profile
}: ReportsProps) {
  // Top-level report section toggler
  const [activeReportTab, setActiveReportTab] = useState<'financeiro' | 'estoque'>('financeiro');

  // Filters State for Financial section
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedAccTypeId, setSelectedAccTypeId] = useState<string>('todos');

  // Filters State for Stock section
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [selectedStockCategoryId, setSelectedStockCategoryId] = useState('todas');
  const [showOnlyLowStock, setShowOnlyLowStock] = useState(false);

  // Colors Palette for charts
  const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  // Helper date function
  const isInDateRange = (dateStr: string) => {
    if (!startDate && !endDate) return true;
    const targetDate = new Date(dateStr);
    if (startDate) {
      const start = new Date(startDate);
      if (targetDate < start) return false;
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (targetDate > end) return false;
    }
    return true;
  };

  // Filtered reservations
  const filteredReservations = useMemo(() => {
    return reservations.filter(res => {
      const dateOk = isInDateRange(res.checkInDate);
      if (!dateOk) return false;

      if (selectedAccTypeId !== 'todos') {
        const acc = accommodations.find(a => a.id === res.accommodationId);
        if (acc?.typeId !== selectedAccTypeId) return false;
      }
      return true;
    });
  }, [reservations, startDate, endDate, selectedAccTypeId, accommodations]);

  // Financial statistics
  const stats = useMemo(() => {
    let totalRevenue = 0;
    let stayRevenue = 0;
    let consumptionRevenue = 0;
    let additionalRevenue = 0;
    
    let totalNights = 0;
    let totalGuests = 0;
    let nonCancelledCount = 0;
    let completedStayCount = 0;
    let occupancyActiveCount = 0;

    filteredReservations.forEach(res => {
      if (res.status === 'Cancelado') return;

      nonCancelledCount++;
      totalGuests += res.guestsCount || 1;

      const inDate = new Date(res.checkInDate);
      const outDate = new Date(res.checkOutDate);
      const diffTime = Math.abs(outDate.getTime() - inDate.getTime());
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
      totalNights += nights;

      stayRevenue += res.totalStayAmount || (nights * (res.dailyRate || 0));
      consumptionRevenue += res.totalConsumptionAmount || 0;
      additionalRevenue += res.totalAdditionalAmount || 0;

      if (res.status === 'Finalizado') {
        completedStayCount++;
      }
      if (res.status === 'Iniciado') {
        occupancyActiveCount++;
      }
    });

    totalRevenue = stayRevenue + consumptionRevenue + additionalRevenue;

    const averageDailyRate = totalNights > 0 ? (stayRevenue / totalNights) : 0;
    const averageStayLength = nonCancelledCount > 0 ? (totalNights / nonCancelledCount) : 0;
    const averageTicket = nonCancelledCount > 0 ? (totalRevenue / nonCancelledCount) : 0;

    const totalActiveRooms = accommodations.filter(a => a.active).length;
    const currentOccupancyRate = totalActiveRooms > 0 ? (occupancyActiveCount / totalActiveRooms) * 100 : 0;

    return {
      totalRevenue,
      stayRevenue,
      consumptionRevenue,
      additionalRevenue,
      totalNights,
      totalGuests,
      nonCancelledCount,
      completedStayCount,
      averageDailyRate,
      averageStayLength,
      averageTicket,
      currentOccupancyRate,
      occupancyActiveCount,
      totalActiveRooms
    };
  }, [filteredReservations, accommodations]);

  // Consumables consumed statistics (Top products)
  const consumablesStats = useMemo(() => {
    const itemMap: { [key: string]: { name: string, quantity: number, total: number } } = {};

    filteredReservations.forEach(res => {
      if (res.status === 'Cancelado') return;
      asArray(res.consumptions).forEach(c => {
        if (c.type === 'frigobar' && c.itemId) {
          const item = stockItems.find(s => s.id === c.itemId) || { name: c.description };
          if (!itemMap[c.itemId]) {
            itemMap[c.itemId] = {
              name: item.name,
              quantity: 0,
              total: 0
            };
          }
          itemMap[c.itemId].quantity += c.quantity;
          itemMap[c.itemId].total += c.total;
        }
      });
    });

    return Object.values(itemMap).sort((a,b) => b.total - a.total).slice(0, 10);
  }, [filteredReservations, stockItems]);

  // Revenue monthly trend helper
  const monthlyRevenueData = useMemo(() => {
    const monthFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'short', year: '2-digit' });
    const monthMap: { [key: string]: { month: string, stay: number, consumption: number, additional: number, total: number, timestamp: number } } = {};

    filteredReservations.forEach(res => {
      if (res.status === 'Cancelado') return;
      const resDate = new Date(res.checkInDate);
      const label = monthFormatter.format(resDate);

      const inDate = new Date(res.checkInDate);
      const outDate = new Date(res.checkOutDate);
      const diffTime = Math.abs(outDate.getTime() - inDate.getTime());
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;

      const stay = res.totalStayAmount || (nights * (res.dailyRate || 0));
      const consumption = res.totalConsumptionAmount || 0;
      const additional = res.totalAdditionalAmount || 0;

      if (!monthMap[label]) {
        const firstDay = new Date(resDate.getFullYear(), resDate.getMonth(), 1);
        monthMap[label] = {
          month: label.toUpperCase(),
          stay: 0,
          consumption: 0,
          additional: 0,
          total: 0,
          timestamp: firstDay.getTime()
        };
      }

      monthMap[label].stay += stay;
      monthMap[label].consumption += consumption;
      monthMap[label].additional += additional;
      monthMap[label].total += (stay + consumption + additional);
    });

    return Object.values(monthMap).sort((a,b) => a.timestamp - b.timestamp);
  }, [filteredReservations]);

  // Reservation Status percentages
  const statusPieData = useMemo(() => {
    const counts = { Pendente: 0, Iniciado: 0, Finalizado: 0, Cancelado: 0 };
    filteredReservations.forEach(res => {
      counts[res.status] = (counts[res.status] || 0) + 1;
    });

    return [
      { name: 'Confirmada (Pendente)', value: counts.Pendente, color: '#3b82f6' },
      { name: 'Estadia Ativa', value: counts.Iniciado, color: '#10b981' },
      { name: 'Finalizada', value: counts.Finalizado, color: '#6366f1' },
      { name: 'Cancelada', value: counts.Cancelado, color: '#ef4444' }
    ].filter(item => item.value > 0);
  }, [filteredReservations]);

  // Highest spending guest profiles
  const topGuestsRanking = useMemo(() => {
    const guestMap: { [key: string]: { name: string, document: string, reservationsCount: number, totalSpend: number } } = {};

    filteredReservations.forEach(res => {
      if (res.status === 'Cancelado') return;
      const client = clients.find(c => c.id === res.clientId);
      if (!client) return;

      if (!guestMap[res.clientId]) {
        guestMap[res.clientId] = {
          name: client.name,
          document: client.document,
          reservationsCount: 0,
          totalSpend: 0
        };
      }

      guestMap[res.clientId].reservationsCount++;
      const inDate = new Date(res.checkInDate);
      const outDate = new Date(res.checkOutDate);
      const diffTime = Math.abs(outDate.getTime() - inDate.getTime());
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;

      const total = (res.totalStayAmount || (nights * (res.dailyRate || 0))) + 
                    (res.totalConsumptionAmount || 0) + 
                    (res.totalAdditionalAmount || 0);
      
      guestMap[res.clientId].totalSpend += total;
    });

    return Object.values(guestMap).sort((a,b) => b.totalSpend - a.totalSpend).slice(0, 5);
  }, [filteredReservations, clients]);

  // Top profitable Rooms
  const topAccommodationsRanking = useMemo(() => {
    const accMap: { [key: string]: { name: string, typeName: string, active: boolean, timesBooked: number, totalRevenue: number } } = {};

    filteredReservations.forEach(res => {
      if (res.status === 'Cancelado') return;
      const acc = accommodations.find(a => a.id === res.accommodationId);
      if (!acc) return;

      const typeName = accommodationTypes.find(t => t.id === acc.typeId)?.name || 'Quarto';

      if (!accMap[res.accommodationId]) {
        accMap[res.accommodationId] = {
          name: `${typeName} ${acc.number}`,
          typeName,
          active: acc.active,
          timesBooked: 0,
          totalRevenue: 0
        };
      }

      accMap[res.accommodationId].timesBooked++;
      const inDate = new Date(res.checkInDate);
      const outDate = new Date(res.checkOutDate);
      const diffTime = Math.abs(outDate.getTime() - inDate.getTime());
      const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;

      const total = (res.totalStayAmount || (nights * (res.dailyRate || 0))) + 
                    (res.totalConsumptionAmount || 0) + 
                    (res.totalAdditionalAmount || 0);

      accMap[res.accommodationId].totalRevenue += total;
    });

    return Object.values(accMap).sort((a,b) => b.totalRevenue - a.totalRevenue).slice(0, 5);
  }, [filteredReservations, accommodations, accommodationTypes]);

  // ==========================================
  // INVENTORY / STOCK REPORT LOGIC & COMPUTATIONS
  // ==========================================

  // Total quantity of active physical items in central stock
  const totalCentralUnits = useMemo(() => {
    return stockItems.filter(item => item.active).reduce((sum, item) => sum + item.quantity, 0);
  }, [stockItems]);

  // Potential revenue valuation of all central stock (Sales standard)
  const totalStockValuation = useMemo(() => {
    return stockItems.filter(item => item.active).reduce((sum, item) => sum + (item.quantity * item.price), 0);
  }, [stockItems]);

  // Distinct types of physical items configured
  const totalDistinctItems = useMemo(() => {
    return stockItems.filter(item => item.active).length;
  }, [stockItems]);

  // Critical items below standard margin (quantity <= 5)
  const criticalStockItems = useMemo(() => {
    return stockItems.filter(item => item.active && item.quantity <= 5);
  }, [stockItems]);

  // Total units currently distributed inside all active minibars
  const totalMinibarDistributedUnits = useMemo(() => {
    let sum = 0;
    minibars.filter(m => m.active).forEach(m => {
      asArray(m.items).forEach(i => {
        sum += i.quantity;
      });
    });
    return sum;
  }, [minibars]);

  // Items by category counts for bar graphs
  const stockByCategoryChartData = useMemo(() => {
    const map: { [key: string]: number } = {};
    stockItems.filter(item => item.active).forEach(item => {
      const categoryName = stockCategories.find(c => c.id === item.categoryId)?.name || 'Outros';
      map[categoryName] = (map[categoryName] || 0) + item.quantity;
    });
    return Object.entries(map).map(([name, Unidades]) => ({ name, Unidades }));
  }, [stockItems, stockCategories]);

  // Filtered Inventory list for the interactive table
  const filteredStockList = useMemo(() => {
    return stockItems.filter(item => {
      if (!item.active) return false;
      
      // search filter
      const matchesSearch = item.name.toLowerCase().includes(stockSearchQuery.toLowerCase());
      
      // category filter
      const matchesCategory = selectedStockCategoryId === 'todas' || item.categoryId === selectedStockCategoryId;
      
      // alarm filter
      const matchesLowFilter = !showOnlyLowStock || item.quantity <= 5;

      return matchesSearch && matchesCategory && matchesLowFilter;
    });
  }, [stockItems, stockSearchQuery, selectedStockCategoryId, showOnlyLowStock]);

  // Complete overview of all minibars and their current balance (checklists)
  const roomMinibarsChecklist = useMemo(() => {
    return minibars.map(mb => {
      const room = accommodations.find(a => a.id === mb.accommodationId);
      const roomType = room ? (accommodationTypes.find(t => t.id === room.typeId)?.name || 'Quarto') : 'Quarto';
      const label = room ? `${roomType} ${room.number}` : `Frigobar tag: ${mb.tag}`;
      const safeItems = Array.isArray(mb.items) ? mb.items : (typeof mb.items === 'string' ? JSON.parse(mb.items || '[]') : []);
      const totalUnits = safeItems.reduce((sum, i) => sum + i.quantity, 0);

      const parsedItems = safeItems.map(mbi => {
        const item = stockItems.find(s => s.id === mbi.itemId) || { name: 'Item indefinido', price: 0 };
        return {
          name: item.name,
          quantity: mbi.quantity,
          value: mbi.quantity * item.price
        };
      });

      return {
        id: mb.id,
        tag: mb.tag,
        label,
        active: mb.active,
        totalUnits,
        items: parsedItems
      };
    });
  }, [minibars, accommodations, accommodationTypes, stockItems]);


  // ==========================================
  // PDF EXPORTING handlers
  // ==========================================

  // Export 1: Financial and Operational Insights
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      const primaryColor = [15, 23, 42]; 
      const accentColor = [79, 70, 229]; 
  
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, 210, 40, 'F');
  
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text(profile.name.toUpperCase(), 15, 18);
  
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`RELATÓRIO GERENCIAL ANALÍTICO COMPLETO - DESEMPENHO E FINANÇAS`, 15, 25);
      doc.text(`Unidade: ${profile.corporateName} | CNPJ: ${profile.document || 'N/D'}`, 15, 30);
      doc.text(`Gerado em: ${new Date().toLocaleString()}`, 15, 35);
  
      let currentY = 52;
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text("1. PARÂMETROS E FILTROS APLICADOS", 15, currentY);
      currentY += 8;
  
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      const filterRange = (startDate || endDate) 
        ? `Período selecionado: ${startDate ? new Date(startDate).toLocaleDateString() : 'Início'} até ${endDate ? new Date(endDate).toLocaleDateString() : 'Fim'}`
        : 'Período selecionado: Histórico Geral Completo';
  
      const filterAccType = selectedAccTypeId !== 'todos'
        ? `Categoria: ${accommodationTypes.find(t => t.id === selectedAccTypeId)?.name || 'Específica'}`
        : 'Categoria: Todas as categorias de acomodação';
  
      doc.text(`- ${filterRange}`, 18, currentY);
      currentY += 6;
      doc.text(`- ${filterAccType}`, 18, currentY);
      currentY += 12;
  
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setFillColor(248, 250, 252); 
      doc.rect(15, currentY - 5, 180, 48, 'F');
      
      doc.text("2. RESUMO OPERACIONAL E FINANCEIRO DO PERÍODO", 15, currentY);
      currentY += 8;
  
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.text(`Faturamento Bruto Total: R$ ${stats.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 20, currentY);
      doc.text(`Hóspedes Atendidos: ${stats.totalGuests} paxs`, 115, currentY);
      currentY += 7;
  
      doc.text(`- Faturamento de Estadia: R$ ${stats.stayRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 22, currentY);
      doc.text(`Total de Noites Vendidas: ${stats.totalNights}`, 115, currentY);
      currentY += 7;
  
      doc.text(`- Vendas de Frigobar: R$ ${stats.consumptionRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 22, currentY);
      doc.text(`Estadias Reservadas: ${stats.nonCancelledCount} reservas`, 115, currentY);
      currentY += 7;
  
      doc.text(`- Taxas e Adicionais: R$ ${stats.additionalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 22, currentY);
      doc.text(`Ticket Médio por Reserva: R$ ${stats.averageTicket.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 115, currentY);
      currentY += 12;
  
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text("3. INDICADORES GARGALOS E PERFORMANCE UNITÁRIA", 15, currentY);
      currentY += 8;
  
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.text(`- Diária Média Praticada (ADR): R$ ${stats.averageDailyRate.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 18, currentY);
      currentY += 6;
      doc.text(`- Tempo Médio de Permanência (Average Stay): ${stats.averageStayLength.toFixed(1.5)} dia(s)`, 18, currentY);
      currentY += 6;
      doc.text(`- Taxa de Ocupação Atual das Acomodações: ${stats.currentOccupancyRate.toFixed(1)}% (${stats.occupancyActiveCount} de ${stats.totalActiveRooms} acomodações ativas ocupadas)`, 18, currentY);
      currentY += 14;
  
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text("4. PERFORMANCE DAS ACOMODAÇÕES (TOP 5 MAIS RENTÁVEIS)", 15, currentY);
      currentY += 7;
  
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(15, currentY - 4, 180, 6.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.text("Acomodação / Tipo", 18, currentY);
      doc.text("Qtd. de Reservas", 115, currentY);
      doc.text("Receita Acumulada", 155, currentY);
      currentY += 7;
  
      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'normal');
      if (topAccommodationsRanking.length === 0) {
        doc.text("Nenhuma acomodação ativa registrada no período.", 18, currentY);
        currentY += 6;
      } else {
        topAccommodationsRanking.forEach(item => {
          doc.text(item.name, 18, currentY);
          doc.text(`${item.timesBooked} reserva(s)`, 115, currentY);
          doc.text(`R$ ${item.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 155, currentY);
          currentY += 6;
        });
      }
      currentY += 8;
  
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text("5. TOP CONSUMOS DE PRODUTOS DO FRIGOBAR", 15, currentY);
      currentY += 7;
  
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(15, currentY - 4, 180, 6.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.text("Item Consumido", 18, currentY);
      doc.text("Unidades Retiradas", 115, currentY);
      doc.text("Total Faturado", 155, currentY);
      currentY += 7;
  
      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'normal');
      if (consumablesStats.length === 0) {
        doc.text("Sem registros de retirada ou faturamento de frigobar no período.", 18, currentY);
        currentY += 6;
      } else {
        consumablesStats.slice(0, 5).forEach(item => {
          doc.text(item.name, 18, currentY);
          doc.text(`${item.quantity} un`, 115, currentY);
          doc.text(`R$ ${item.total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 155, currentY);
          currentY += 6;
        });
      }
  
      doc.addPage();
      let page2Y = 25;
  
      doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.rect(0, 0, 210, 5, 'F');
  
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text("6. RANKING DE HÓSPEDES POR GASTO ACUMULADO (TOP 5)", 15, page2Y);
      page2Y += 7;
  
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(15, page2Y - 4, 180, 6.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.text("Hóspede", 18, page2Y);
      doc.text("Documento (CPF)", 80, page2Y);
      doc.text("Visitas", 140, page2Y);
      doc.text("Consumo Total", 165, page2Y);
      page2Y += 7;
  
      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'normal');
      if (topGuestsRanking.length === 0) {
        doc.text("Nenhum hóspede faturado no período filtro.", 18, page2Y);
        page2Y += 6;
      } else {
        topGuestsRanking.forEach(guest => {
          doc.text(guest.name, 18, page2Y);
          doc.text(guest.document || 'N/A', 80, page2Y);
          doc.text(`${guest.reservationsCount} reserva(s)`, 140, page2Y);
          doc.text(`R$ ${guest.totalSpend.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 165, page2Y);
          page2Y += 6;
        });
      }
      page2Y += 12;
  
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text("7. CONCLUSÕES E PARECER TÉCNICO OPERACIONAL", 15, page2Y);
      page2Y += 6;
  
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      
      const rateText = stats.currentOccupancyRate > 75 
        ? "ALTA OPERAÇÃO: Os níveis de ocupação estão elevados. Recomenda-se reforçar a escala de limpeza e controle de estoque de reposição de frigobar." 
        : stats.currentOccupancyRate > 40
        ? "OPERAÇÃO MODERADA: Equilíbrio padrão operacional. Foco focado em promoções cruzadas e ofertas de consumo interno."
        : "BAIXA OPERAÇÃO (Ociosidade): Alerta para canais de venda direta. Atente para manutenção programada preventiva nas acomodações disponíveis.";
  
      doc.text(rateText, 15, page2Y, { maxWidth: 180 });
      page2Y += 12;
  
      const ADRText = stats.averageDailyRate > 300
        ? "PREÇO DE DIÁRIA ALTO: Excelente nível de faturamento médio de estadias. Mantenha os serviços de hospitalidade correspondentes às expectativas premium."
        : "PREÇO DE DIÁRIA INTERMEDIÁRIO/BAIXO: Avalie reajuste estratégico nos finais de semana ou pacotes temáticos para aumentar a diária média (ADR).";
      doc.text(ADRText, 15, page2Y, { maxWidth: 180 });
      page2Y += 16;
  
      doc.setDrawColor(226, 232, 240);
      doc.line(15, page2Y, 90, page2Y);
      doc.line(120, page2Y, 195, page2Y);
      page2Y += 4;
  
      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text("GERENTE OPERACIONAL", 15, page2Y);
      doc.text("CONTROLE PATRIMONIAL", 120, page2Y);
      page2Y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(profile.name, 15, page2Y);
      doc.text(profile.corporateName, 120, page2Y);
  
      doc.save(`Relatorio_Financeiro_${profile.name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Houve um erro técnico interno ao gerar este PDF.");
    }
  };

  // Export 2: Complete Inventory, Low Stock warnings and Minibars Distribution
  const handleExportStockPDF = () => {
    try {
      const doc = new jsPDF();
      const primaryColor = [15, 23, 42]; 
      const alertColor = [220, 38, 38]; // Red
      const secondaryColor = [30, 41, 59];
  
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, 210, 40, 'F');
  
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text(profile.name.toUpperCase(), 15, 18);
  
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`RELATÓRIO DE INVENTÁRIO, CONTROLE DE ESTOQUE E FRIGOBARES`, 15, 25);
      doc.text(`Unidade: ${profile.corporateName} | CNPJ: ${profile.document || 'N/D'}`, 15, 30);
      doc.text(`Documento emitido em: ${new Date().toLocaleString()}`, 15, 35);
  
      let currentY = 50;
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("1. CONSOLIDADO DA GESTÃO DE ESTOQUES", 15, currentY);
      currentY += 8;
  
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setFillColor(248, 250, 252); 
      doc.rect(15, currentY - 5, 180, 28, 'F');

      doc.text(`Variedade de Produtos Configurados: ${totalDistinctItems} produtos`, 20, currentY);
      doc.text(`Total de Peças no Estoque Central: ${totalCentralUnits} unidades`, 110, currentY);
      currentY += 7;
      doc.text(`Valoração do Inventário (Venda Estimada): R$ ${totalStockValuation.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 20, currentY);
      doc.text(`Unidades já nos Frigobares: ${totalMinibarDistributedUnits} un(s)`, 110, currentY);
      currentY += 7;
      doc.text(`Produtos em Alerta Crítico (<= 5 un): ${pluralizar(criticalStockItems.length, 'item', 'itens')}`, 20, currentY);
      currentY += 12;

      // Section 2: Low Stock warnings table
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(220, 38, 38);
      doc.text("2. ALERTA DE REPOSIÇÃO IMEDIATA (ESTOQUE CRÍTICO)", 15, currentY);
      currentY += 6;

      doc.setFillColor(220, 38, 38);
      doc.rect(15, currentY - 4, 180, 6, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text("Produto", 18, currentY);
      doc.text("Categoria", 85, currentY);
      doc.text("Saldo Central", 135, currentY);
      doc.text("Valor Unitário", 165, currentY);
      currentY += 6;

      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'normal');

      if (criticalStockItems.length === 0) {
        doc.text("Excelente! Nenhum item físico com saldo abaixo do limiar de segurança (5 un).", 18, currentY);
        currentY += 6;
      } else {
        criticalStockItems.forEach(item => {
          const cat = stockCategories.find(c => c.id === item.categoryId)?.name || 'Outras';
          doc.text(item.name, 18, currentY);
          doc.text(cat, 85, currentY);
          doc.setTextColor(220, 38, 38);
          doc.setFont('helvetica', 'bold');
          doc.text(`${item.quantity} un (ABAIXO DO MÍNIMO)`, 135, currentY);
          doc.setTextColor(51, 65, 85);
          doc.setFont('helvetica', 'normal');
          doc.text(`R$ ${item.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 165, currentY);
          currentY += 6;
        });
      }
      currentY += 10;

      // Section 3: Central Stock list
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text("3. POSIÇÃO DE SALDO GERAL - ESTOQUE CENTRAL", 15, currentY);
      currentY += 6;

      doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.rect(15, currentY - 4, 180, 6, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text("Produto", 18, currentY);
      doc.text("Categoria", 85, currentY);
      doc.text("Saldo Físico", 135, currentY);
      doc.text("Ativo?", 165, currentY);
      currentY += 6;

      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'normal');

      const nonCriticalItemsToShow = stockItems.filter(i => i.active && i.quantity > 5).slice(0, 15);
      const listToShow = [...criticalStockItems, ...nonCriticalItemsToShow];

      listToShow.forEach(item => {
        if (currentY > 275) {
          doc.addPage();
          currentY = 25;
        }
        const cat = stockCategories.find(c => c.id === item.categoryId)?.name || 'Outros';
        doc.text(item.name, 18, currentY);
        doc.text(cat, 85, currentY);
        doc.setFont('helvetica', item.quantity <= 5 ? 'bold' : 'normal');
        doc.text(`${item.quantity} un`, 135, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text("Sim", 165, currentY);
        currentY += 5.5;
      });

      // Page Addition for Minibar Distribution Checklists
      doc.addPage();
      let p2Y = 25;

      doc.setFillColor(0, 163, 108); // Green
      doc.rect(0, 0, 210, 5, 'F');

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("4. MAPA DE REPOSIÇÃO E STATUS DE FRIGOBARES POR ACOMODAÇÃO", 15, p2Y);
      p2Y += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text("Checklist de acompanhamento para governança/camareiras realizarem reposições diárias:", 15, p2Y);
      p2Y += 6;

      doc.setTextColor(15, 23, 42);

      roomMinibarsChecklist.forEach(item => {
        if (p2Y > 265) {
          doc.addPage();
          p2Y = 25;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setFillColor(241, 245, 249);
        doc.rect(15, p2Y - 4, 180, 5.5, 'F');
        doc.text(`${item.label} (Patrimônio: ${item.tag})`, 18, p2Y);
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'normal');
        doc.text(`Produtos: ${item.totalUnits} peças`, 150, p2Y);
        p2Y += 5.5;

        if (item.items.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(148, 163, 184);
          doc.text("  - Frigobar vazio ou sem itens alocados no momento.", 22, p2Y);
          doc.setTextColor(15, 23, 42);
          doc.setFont('helvetica', 'normal');
          p2Y += 5.5;
        } else {
          item.items.forEach(subItem => {
            if (p2Y > 275) {
              doc.addPage();
              p2Y = 25;
            }
            doc.setFontSize(8.5);
            doc.text(`   [  ]  ${subItem.name}`, 22, p2Y);
            doc.text(`Quantidade Atual: ${subItem.quantity} un`, 110, p2Y);
            doc.text(`Valor Estimado: R$ ${subItem.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 150, p2Y);
            p2Y += 5;
          });
        }
        p2Y += 3;
      });

      if (p2Y > 250) {
        doc.addPage();
        p2Y = 25;
      }

      p2Y += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text("5. RECOMENDAÇÕES DA GOVERNANÇA PATRIMONIAL", 15, p2Y);
      p2Y += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      const inventoryStatusText = criticalStockItems.length > 3
        ? "ALERTA ROUBADO/FALTA DE REPOSIÇÃO: Existem múltiplos itens estratégicos com saldo muito baixo no almoxarifado central. Solicite compra urgente com fornecedores parceiros."
        : "ESTOQUE CONTROLADO E SEGURO: A reposição interna de frigobares das acomodações está sob controle operacional satisfatório.";
      doc.text(inventoryStatusText, 15, p2Y, { maxWidth: 180 });
      p2Y += 15;

      doc.setDrawColor(226, 232, 240);
      doc.line(15, p2Y, 90, p2Y);
      doc.line(120, p2Y, 195, p2Y);
      p2Y += 4;
  
      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text("SUPERVISORA DE GOVERNANÇA", 15, p2Y);
      doc.text("GERENTE DE COMPRAS", 120, p2Y);

      doc.save(`Ficha_Estoque_Geral_${profile.name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Houve um erro técnico interno ao gerar o documento PDF de estoque.");
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Dynamic Navigation Subheader Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 relative overflow-hidden shadow-xl border border-slate-800">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <TrendingUp className="w-48 h-48 text-indigo-500" />
        </div>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-indigo-500/20 text-indigo-400 text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full border border-indigo-400/20">
                Hospitalidade 360
              </span>
              <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full border border-emerald-400/20">
                Auditoria Precisa
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl font-black tracking-tight font-sans">
              Relatórios e Indicadores
            </h1>
            <p className="text-zinc-300 text-xs mt-1.5 max-w-xl font-normal">
              Opere com máxima segurança e clareza. Navegue abaixo entre as visões Financeira e de Controle de Estoque de Alimentos/Frigobares.
            </p>
          </div>
          
          <button
            type="button"
            id="btn-main-pdf-export"
            onClick={activeReportTab === 'financeiro' ? handleExportPDF : handleExportStockPDF}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg hover:shadow-indigo-500/20 active:scale-95 cursor-pointer shrink-0"
          >
            <Download className="w-4 h-4" /> Exportar {activeReportTab === 'financeiro' ? 'Finanças' : 'Estoques'} em PDF
          </button>
        </div>

        {/* Interior Navigation tab indicators */}
        <div className="flex items-center gap-1.5 mt-8 border-t border-slate-800 pt-4">
          <button
            type="button"
            id="tab-financial-report-view"
            onClick={() => setActiveReportTab('financeiro')}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
              activeReportTab === 'financeiro' 
                ? 'bg-indigo-600 text-white shadow-xs' 
                : 'bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white'
            }`}
          >
            📊 Hospedagem & Finanças
          </button>
          
          <button
            type="button"
            id="tab-stock-report-view"
            onClick={() => setActiveReportTab('estoque')}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
              activeReportTab === 'estoque' 
                ? 'bg-indigo-600 text-white shadow-xs' 
                : 'bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white'
            }`}
          >
            📦 Estoque & Inventário de Frigobares
          </button>
        </div>
      </div>

      {/* =======================================================
          VIEW 1: FINANCEIRO & HOSPEDAGEM
          ======================================================= */}
      {activeReportTab === 'financeiro' && (
        <div className="space-y-6">
          
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5">
            <h2 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-indigo-500" /> Filtros de Estadias & Faturamento
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1.5">Categorias de Acomodação</label>
                <select
                  value={selectedAccTypeId}
                  onChange={(e) => setSelectedAccTypeId(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 hover:border-zinc-300 text-zinc-800 text-xs font-medium rounded-lg p-2.5 outline-none transition-colors cursor-pointer"
                >
                  <option value="todos">Todas as Categorias</option>
                  {accommodationTypes.filter(t => t.active).map(types => (
                    <option key={types.id} value={types.id}>{types.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1.5">Data Inicial</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 hover:border-zinc-300 text-zinc-800 text-xs font-medium rounded-lg p-2.5 outline-none transition-colors cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1.5">Data Final</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 hover:border-zinc-300 text-zinc-800 text-xs font-medium rounded-lg p-2.5 outline-none transition-colors cursor-pointer"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setSelectedAccTypeId('todos');
                  }}
                  className="w-full py-2.5 bg-zinc-100 hover:bg-zinc-200 border border-zinc-200/80 text-zinc-700 font-extrabold text-xs uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Faturamento Bruto */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Faturamento Bruto
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-slate-800 font-mono">
                    R$ {stats.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h3>
                </div>
                <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <DollarSign className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-3 gap-1 text-[10px] text-zinc-550">
                <div>
                  <span className="block font-medium text-xxs text-zinc-400 uppercase">Estadias</span>
                  <span className="font-bold text-slate-800 font-mono">R$ {stats.stayRevenue.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</span>
                </div>
                <div>
                  <span className="block font-medium text-xxs text-zinc-400 uppercase">Frigobar</span>
                  <span className="font-bold text-slate-800 font-mono flex items-center gap-0.5"><Coffee className="w-2.5 h-2.5" />R$ {stats.consumptionRevenue.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</span>
                </div>
                <div>
                  <span className="block font-medium text-xxs text-zinc-400 uppercase">Extras</span>
                  <span className="font-bold text-slate-800 font-mono">R$ {stats.additionalRevenue.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</span>
                </div>
              </div>
            </div>

            {/* Diária Média (ADR) */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Diária Média (ADR)
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-slate-800 font-mono">
                    R$ {stats.averageDailyRate.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h3>
                </div>
                <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Building2 className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
                <span>Ticket médio p/ reserva</span>
                <span className="font-extrabold text-slate-800 font-mono">R$ {stats.averageTicket.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</span>
              </div>
            </div>

            {/* Ocupação */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Taxa de Ocupação Atual
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-slate-800 font-mono">
                    {stats.currentOccupancyRate.toFixed(1)}%
                  </h3>
                </div>
                <div className="p-2.5 bg-amber-50 text-amber-600 rounded-lg">
                  <Percent className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
                <span>{stats.occupancyActiveCount} de {stats.totalActiveRooms} acomodações ocupadas</span>
                <span className="inline-flex px-1.5 py-0.2 rounded text-[10px] font-bold bg-amber-50 text-amber-700">EM CURSO</span>
              </div>
            </div>

            {/* Total Guests */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Volume Geral
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-slate-800 font-mono">
                    {stats.totalGuests} Paxs
                  </h3>
                </div>
                <div className="p-2.5 bg-purple-50 text-purple-600 rounded-lg">
                  <Users className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-2 gap-1 text-[11px] text-zinc-500">
                <div>
                  <span className="block font-medium text-xxs text-zinc-400">ESTADIAS</span>
                  <span className="font-extrabold text-slate-800 font-mono">{stats.nonCancelledCount} reservas</span>
                </div>
                <div>
                  <span className="block font-medium text-xxs text-zinc-400">NOITES VENDIDAS</span>
                  <span className="font-extrabold text-slate-800 font-mono">{stats.totalNights} d.m</span>
                </div>
              </div>
            </div>

          </div>

          {/* Primary Graphs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-sm font-extrabold text-zinc-800 uppercase tracking-wide">
                    Faturamento Histórico Consolidado
                  </h2>
                  <p className="text-zinc-400 text-xxs mt-0.5">
                    Valores divididos por diárias, faturamentos de frigobares e adicionais.
                  </p>
                </div>
              </div>

              <div className="h-72">
                {monthlyRevenueData.length === 0 ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-50 rounded-xl border border-dashed border-zinc-200">
                    <Calendar className="w-8 h-8 text-zinc-300 mb-2" />
                    <span className="text-zinc-400 text-xs font-semibold">Sem faturamento suficiente no critério escolhido</span>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={monthlyRevenueData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#4f46e5" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} tickLine={false} />
                      <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', borderColor: '#e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }} 
                        formatter={(value: any) => [`R$ ${Number(value).toLocaleString('pt-BR')}`, '']}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                      <Area type="monotone" name="Faturamento de Estadia" dataKey="stay" stroke="#4f46e5" fillOpacity={1} fill="url(#colorTotal)" strokeWidth={2.5} />
                      <Area type="monotone" name="Frigobar" dataKey="consumption" stroke="#10b981" fillOpacity={0} strokeWidth={2} />
                      <Area type="monotone" name="Extras & Serviços" dataKey="additional" stroke="#f59e0b" fillOpacity={0} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col">
              <div>
                <h2 className="text-sm font-extrabold text-zinc-800 uppercase tracking-wide mb-1">
                  Reservas por Status
                </h2>
                <p className="text-zinc-400 text-xxs">
                  Disposição e saúde de agendamento geral no sistema.
                </p>
              </div>

              <div className="flex-1 min-h-[220px] flex items-center justify-center bg-zinc-50/50 my-3 rounded-lg border border-dashed border-zinc-150">
                {statusPieData.length === 0 ? (
                  <div className="text-zinc-350 text-xs flex flex-col items-center">
                    <FileText className="w-8 h-8 mb-1" />
                    Sem registros
                  </div>
                ) : (
                  <div className="relative w-full h-full max-h-[230px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={75}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {statusPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: any) => [`${value} reserva(s)`, '']} />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-2xl font-black text-slate-800 font-mono">
                        {filteredReservations.length}
                      </span>
                      <span className="text-[9px] uppercase font-bold text-zinc-400 tracking-wider">
                        Total
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1.5 bg-zinc-50 p-3 rounded-lg border border-zinc-150/50">
                {statusPieData.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="text-zinc-650 truncate font-medium">{s.name}</span>
                    </div>
                    <span className="font-bold text-zinc-800 font-mono shrink-0">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Rankings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Rentáveis */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-zinc-100">
                <div>
                  <h2 className="text-sm font-extrabold text-zinc-800 uppercase tracking-wide flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-indigo-500 rounded-sm" /> Top 5 Acomodações Rentáveis
                  </h2>
                  <p className="text-zinc-400 text-xxs mt-0.5">
                    Suítes e quartos com maior faturamento histórico acumulado de estadias.
                  </p>
                </div>
                <AwardBadge />
              </div>

              <div className="space-y-3">
                {topAccommodationsRanking.length === 0 ? (
                  <div className="py-8 text-center text-zinc-400 text-xs">Sem estadias registradas ainda.</div>
                ) : (
                  topAccommodationsRanking.map((acc, index) => {
                    const colors = ['bg-indigo-50 text-indigo-700', 'bg-emerald-50 text-emerald-700', 'bg-amber-50 text-amber-700', 'bg-zinc-50 text-zinc-700', 'bg-zinc-50 text-zinc-700'];
                    return (
                      <div key={index} className="flex items-center justify-between p-2.5 rounded-lg border border-zinc-100 hover:bg-zinc-50/50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`w-6 h-6 flex items-center justify-center font-extrabold text-xs rounded-md ${colors[index] || 'bg-zinc-50 text-zinc-600'}`}>
                            {index + 1}
                          </span>
                          <div className="min-w-0">
                            <span className="block text-xs font-bold text-slate-800 truncate">{acc.name}</span>
                            <span className="block text-[10px] text-zinc-400">{acc.timesBooked === 1 ? '1 hospedagem registrada' : `${acc.timesBooked} hospedagens registradas`}</span>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-slate-800 font-mono tracking-tight shrink-0">
                          R$ {acc.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Recorrentes */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-zinc-100">
                <div>
                  <h2 className="text-sm font-extrabold text-zinc-800 uppercase tracking-wide flex items-center gap-2">
                    <span className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" /> Top 5 Hóspedes Recorrentes
                  </h2>
                  <p className="text-zinc-400 text-xxs mt-0.5">
                    Hóspedes com maior ticket de consumo e permanência financeira na pousada.
                  </p>
                </div>
                <AwardBadge />
              </div>

              <div className="space-y-3">
                {topGuestsRanking.length === 0 ? (
                  <div className="py-8 text-center text-zinc-400 text-xs">Sem hóspedes no momento.</div>
                ) : (
                  topGuestsRanking.map((guest, index) => {
                    const colors = ['bg-indigo-50 text-indigo-700', 'bg-emerald-50 text-emerald-700', 'bg-amber-50 text-amber-700', 'bg-zinc-50 text-zinc-700', 'bg-zinc-50 text-zinc-700'];
                    return (
                      <div key={index} className="flex items-center justify-between p-2.5 rounded-lg border border-zinc-100 hover:bg-zinc-50/50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`w-6 h-6 flex items-center justify-center font-extrabold text-xs rounded-md ${colors[index] || 'bg-zinc-50 text-zinc-600'}`}>
                            {index + 1}
                          </span>
                          <div className="min-w-0">
                            <span className="block text-xs font-bold text-slate-800 truncate">{guest.name}</span>
                            <span className="block text-[10px] text-zinc-400 font-mono">Doc: {guest.document || 'N/A'} | {guest.reservationsCount} reserva(s)</span>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-slate-800 font-mono tracking-tight shrink-0">
                          R$ {guest.totalSpend.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

          </div>

        </div>
      )}


      {/* =======================================================
          VIEW 2: CONTROLES DE ESTOQUE & ALMOXARIFADO DE FRIGOBARES
          ======================================================= */}
      {activeReportTab === 'estoque' && (
        <div className="space-y-6">

          {/* Quick Metrics of Stock */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Valor Estimado do Estoque Central */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Valor de Venda Central
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-slate-800 font-mono">
                    R$ {totalStockValuation.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h3>
                </div>
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
                  <Package className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
                <span>Total de itens cadastrados</span>
                <span className="font-extrabold text-slate-800 font-mono">{totalDistinctItems} tipos</span>
              </div>
            </div>

            {/* Total de Peças Físicas */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Saldo Físico Geral
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-slate-800 font-mono">
                    {totalCentralUnits} unidades
                  </h3>
                </div>
                <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Boxes className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
                <span>Armazenadas no almoxarifado</span>
                <span className="font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded text-[10px]">Disponível</span>
              </div>
            </div>

            {/* Alertas Críticos */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Alertas Críticos Mínimos
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-red-650 font-mono">
                    {pluralizar(criticalStockItems.length, 'produto', 'produtos')}
                  </h3>
                </div>
                <div className="p-2.5 bg-red-50 text-red-600 rounded-lg">
                  <AlertTriangle className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
                <span>Estoque central abaixo de 5 un.</span>
                <span className="inline-flex px-1.5 py-0.2 rounded text-[10px] font-bold bg-red-100 text-red-700 uppercase tracking-tight">Comprar</span>
              </div>
            </div>

            {/* Distribuído nos Frigobares */}
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">
                    Alocado nas Suítes
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-amber-650 font-mono">
                    {totalMinibarDistributedUnits} un(s)
                  </h3>
                </div>
                <div className="p-2.5 bg-amber-50 text-amber-600 rounded-lg">
                  <Compass className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
                <span>Ativos em {minibars.filter(m => m.active).length} Frigobares</span>
                <span className="text-[10px] font-bold text-amber-700 uppercase">Valores Móveis</span>
              </div>
            </div>

          </div>

          {/* Graphics of Stock */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5 lg:col-span-2">
              <h2 className="text-sm font-extrabold text-zinc-805 uppercase tracking-wide mb-1">
                Densidade de Inventário por Categoria de Estoque
              </h2>
              <p className="text-zinc-400 text-xxs mb-4">
                Quantidade em peças físicas acumuladas em cada divisão de consumo da pousada.
              </p>

              <div className="h-64">
                {stockByCategoryChartData.length === 0 ? (
                  <div className="w-full h-full flex items-center justify-center text-zinc-350 text-xs">
                    Cadastre categorias de stock para visualizar gráficos de densidade.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stockByCategoryChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#a1a1aa" fontSize={10} tickLine={false} />
                      <YAxis stroke="#a1a1aa" fontSize={10} tickLine={false} />
                      <Tooltip formatter={(value: any) => [`${value} unidades`, 'Saldo acumulado']} />
                      <Bar dataKey="Unidades" fill="#4f46e5" radius={[4, 4, 0, 0]} maxBarSize={45} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Quick alarming panel */}
            <div className="bg-slate-900 text-white rounded-xl p-5 flex flex-col justify-between">
              <div>
                <span className="bg-red-500/20 text-red-400 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded border border-red-500/30">
                  ⚠️ ALERTA DE COMPRAS
                </span>
                <h3 className="text-sm font-black text-white mt-1 uppercase tracking-wide">Reposições Urgentes</h3>
                <p className="text-slate-300 text-[10px] mt-1 mb-4 leading-normal">
                  Os seguintes insumos estão operando abaixo do limiar de segurança estipulado de 5 unidades na central:
                </p>

                <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                  {criticalStockItems.length === 0 ? (
                    <div className="text-xs text-slate-400 font-medium py-4 text-center">✓ Todo o almoxarifado central encontra-se abastecido!</div>
                  ) : (
                    criticalStockItems.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-white/5 last:border-0">
                        <span className="font-bold text-slate-100 truncate max-w-[140px]" title={item.name}>{item.name}</span>
                        <div className="text-right shrink-0">
                          <span className="font-mono font-extrabold text-red-400 block">{item.quantity} un</span>
                          <span className="text-[9px] text-slate-400 font-normal">Sugerido: +20</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between text-xxs text-slate-400">
                <span>Indicado revisão bi-semanal</span>
                <button
                  type="button"
                  id="btn-stock-pdf-down"
                  onClick={handleExportStockPDF}
                  className="font-bold text-indigo-400 hover:text-indigo-300 uppercase cursor-pointer transition-colors"
                >
                  Exportar Ficha ↩
                </button>
              </div>
            </div>

          </div>

          {/* Table: Interactive Stock analysis list */}
          <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5">
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-sm font-extrabold text-zinc-800 uppercase tracking-wide">Posição Analítica de Almoxarifado</h2>
                <p className="text-zinc-400 text-xxs mt-0.5">Filtre, pesquise e localize quantidades disponíveis para reposição de camareiras.</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                
                {/* Searchbar */}
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-zinc-400">
                    <Search className="w-3.5 h-3.5" />
                  </span>
                  <input
                    type="text"
                    value={stockSearchQuery}
                    onChange={(e) => setStockSearchQuery(e.target.value)}
                    placeholder="Buscar insumo..."
                    className="pl-8 pr-3 py-1.5 bg-zinc-50 border border-zinc-200 text-xs font-semibold rounded-lg focus:outline-none focus:border-indigo-400 w-44"
                  />
                </div>

                {/* Switcher categories */}
                <select
                  value={selectedStockCategoryId}
                  onChange={(e) => setSelectedStockCategoryId(e.target.value)}
                  className="bg-zinc-50 border border-zinc-200 p-1.5 rounded-lg text-[11px] font-bold text-zinc-600 focus:outline-none cursor-pointer"
                >
                  <option value="todas">Todas Categorias</option>
                  {stockCategories.filter(c => c.active).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>

                {/* Alarm checkbox button */}
                <button
                  type="button"
                  id="btn-toggle-low-stock-filter"
                  onClick={() => setShowOnlyLowStock(prev => !prev)}
                  className={`px-3 py-1.5 text-[11px] font-bold uppercase rounded-lg border transition-all cursor-pointer ${
                    showOnlyLowStock 
                      ? 'bg-red-50 text-red-700 border-red-200' 
                      : 'bg-zinc-50 text-zinc-650 hover:bg-zinc-100 border-zinc-200'
                  }`}
                >
                  {showOnlyLowStock ? '⚠️ Apenas Baixos' : 'Todos Saldos'}
                </button>

              </div>
            </div>

            {/* In Stock Grid / list Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-zinc-120 bg-zinc-50/70 text-[10px] uppercase font-bold text-zinc-450 tracking-wider">
                    <th className="py-2.5 px-3">Insumo</th>
                    <th className="py-2.5 px-3">Divisão/Categoria</th>
                    <th className="py-2.5 px-3">Saldo Disponível</th>
                    <th className="py-2.5 px-3">Valor de Venda Unitário</th>
                    <th className="py-2.5 px-3 text-right">Valor Potencial Acumulado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-xs">
                  {filteredStockList.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-zinc-350">
                        Nenhum insumo localizado nos critérios de busca e filtros ativos.
                      </td>
                    </tr>
                  ) : (
                    filteredStockList.map((item, idx) => {
                      const catName = stockCategories.find(c => c.id === item.categoryId)?.name || 'Geral';
                      const isLow = item.quantity <= 5;
                      const hasPlenty = item.quantity >= 15;
                      const totalEstim = item.quantity * item.price;

                      return (
                        <tr key={idx} className="hover:bg-zinc-50/50 transition-colors">
                          <td className="py-3 px-3 font-bold text-slate-800">{item.name}</td>
                          <td className="py-3 px-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-100 text-zinc-600 uppercase">
                              {catName}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              {isLow ? (
                                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                              ) : hasPlenty ? (
                                <span className="w-2 h-2 rounded-full bg-green-500" />
                              ) : (
                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                              )}
                              <span className="font-mono font-bold text-slate-800">{item.quantity} peças</span>
                              {isLow && (
                                <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1 py-0.1 rounded">BAIXO</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 font-mono text-zinc-500">
                            R$ {item.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-bold text-slate-800">
                            R$ {totalEstim.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

          </div>

          {/* Minibars Housekeeping Allocation Guide */}
          <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5">
            <div className="mb-4">
              <h2 className="text-sm font-extrabold text-zinc-805 uppercase tracking-wide flex items-center gap-1.5">
                <Compass className="w-4.5 h-4.5 text-indigo-500" /> Mapa Geral de Reposições de Frigobares (Checklist Governança)
              </h2>
              <p className="text-zinc-400 text-xxs mt-0.5">
                Exibe todos os frigobares cadastrados nas dependências da pousada e seus respectivos itens estocados no momento para rápido reabastecimento.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roomMinibarsChecklist.length === 0 ? (
                <div className="p-8 text-center text-zinc-405 col-span-full">Nenhum frigobar configurado nas acomodações históricas.</div>
              ) : (
                roomMinibarsChecklist.map((mb, idx) => (
                  <div key={idx} className="bg-zinc-50 hover:bg-zinc-100/60 p-4 rounded-xl border border-zinc-200 transition-all flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-3 border-b border-zinc-200 pb-2">
                        <div>
                          <h3 className="text-xs font-black text-slate-850 uppercase">{mb.label}</h3>
                          <span className="text-[10px] font-mono text-zinc-400">Patrimônio: {mb.tag}</span>
                        </div>
                        {mb.active ? (
                          <span className="px-1.5 py-0.2 text-[9px] font-bold tracking-tight rounded-md bg-emerald-100 text-emerald-800 uppercase shrink-0">Ativo</span>
                        ) : (
                          <span className="px-1.5 py-0.2 text-[9px] font-bold tracking-tight rounded-md bg-zinc-200 text-zinc-500 uppercase shrink-0">Inativo</span>
                        )}
                      </div>

                      <div className="space-y-2 mb-4">
                        {!Array.isArray(mb.items) || mb.items.length === 0 ? (
                          <span className="block text-[11px] text-zinc-400 font-medium italic py-1">Vazio - nenhuma unidade preenchida</span>
                        ) : (
                          mb.items.map((sub, sidx) => (
                            <div key={sidx} className="flex justify-between items-center text-[11px]">
                              <span className="font-semibold text-zinc-700 truncate max-w-[130px]" title={sub.name}>✓ {sub.name}</span>
                              <span className="font-bold text-slate-800 font-mono bg-white px-1.5 py-0.2 rounded border border-zinc-200 shrink-0">{sub.quantity} un</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="pt-2 border-t border-dashed border-zinc-200 flex items-center justify-between text-[10px]">
                      <span className="text-zinc-400">Total no Frigorífico:</span>
                      <span className="font-black text-indigo-650 font-mono">{mb.totalUnits} itens</span>
                    </div>
                  </div>
                ))
              )}
            </div>

          </div>

          {/* Historical consumption outlets correlation list */}
          <div className="bg-white rounded-xl shadow-xs border border-zinc-200/80 p-5">
            <h2 className="text-sm font-extrabold text-zinc-800 uppercase tracking-wide mb-1">Insumos Mais Baixados / Consumidos (Padrão de Consumação)</h2>
            <p className="text-zinc-400 text-xxs mb-4">Análise de saída de frigobar com base no faturamento de estadias não canceladas no período.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {consumablesStats.length === 0 ? (
                <div className="p-8 text-center text-zinc-350 bg-zinc-50 border border-dashed border-zinc-205 rounded-xl col-span-full">
                  Sem dados de saídas recentes de frigobar faturados.
                </div>
              ) : (
                consumablesStats.map((item, index) => (
                  <div key={index} className="bg-zinc-50 rounded-xl p-4 border border-zinc-200 relative overflow-hidden">
                    <div className="absolute -top-3 -right-3 w-11 h-11 bg-zinc-100 rounded-full flex items-center justify-center text-zinc-350 font-bold text-xl">
                      {index + 1}
                    </div>
                    <span className="block text-[8px] uppercase font-bold text-indigo-500 tracking-wider mb-1">DEMANDA COLETADA</span>
                    <span className="block text-xs font-extrabold text-slate-850 truncate mb-1" title={item.name}>{item.name}</span>
                    <div className="flex items-end justify-between mt-3">
                      <div>
                        <span className="block text-[8.5px] uppercase font-bold text-zinc-400">RETIRADAS</span>
                        <span className="font-mono text-xs font-black text-slate-800">{item.quantity} un</span>
                      </div>
                      <div className="text-right">
                        <span className="block text-[8.5px] uppercase font-bold text-zinc-400">FATURADO</span>
                        <span className="font-mono text-xs font-bold text-emerald-650 bg-emerald-50 px-1 py-0.5 rounded">
                          R$ {item.total.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      )}

    </div>
  );
}

// Visual layout helper components
function AwardBadge() {
  return (
    <div className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 uppercase bg-indigo-50/70 border border-indigo-100 px-2 py-1 rounded-md shrink-0 select-none">
      📊 Consolidado
    </div>
  );
}
